import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Check, Loader2 } from "lucide-react"

export const HEADER_HEIGHT = "64px"
const SAVE_CLICK_UNLOCK_FALLBACK_MS = 1500

interface FixedHeaderLayoutProps {
  title: string
  backLabel?: string
  onFirstButtonClicked: () => void
  onSecondButtonClicked?: () => void
  onBack?: () => void
  isFirstButtonDisabled?: boolean
  onFirstButtonLoading?: boolean
  onSecondButtonLoading?: boolean
  validateSecondButton?: () => boolean
  secondButtonDisabled?: boolean
  firstButtonText?: string
  secondButtonText?: string
}

export const FixedHeaderLayout: React.FC<FixedHeaderLayoutProps> = ({
  title,
  backLabel,
  onFirstButtonClicked,
  onSecondButtonClicked,
  onBack,
  secondButtonDisabled = false,
  isFirstButtonDisabled = false,
  onSecondButtonLoading = false,
  onFirstButtonLoading = false,
  validateSecondButton,
  firstButtonText,
  secondButtonText,
}) => {
  const { t } = useTranslation()
  const resolvedBackLabel = backLabel ?? t("backToProducts")
  const [isSecondButtonLocked, setIsSecondButtonLocked] = useState(false)
  const secondButtonUnlockTimeoutRef = useRef<number | null>(null)
  const secondButtonLoadingStartedRef = useRef(false)

  useEffect(() => {
    if (onSecondButtonLoading) {
      secondButtonLoadingStartedRef.current = true
      return
    }

    if (!isSecondButtonLocked || !secondButtonLoadingStartedRef.current) return

    secondButtonLoadingStartedRef.current = false
    if (secondButtonUnlockTimeoutRef.current !== null) {
      window.clearTimeout(secondButtonUnlockTimeoutRef.current)
      secondButtonUnlockTimeoutRef.current = null
    }
    setIsSecondButtonLocked(false)
  }, [isSecondButtonLocked, onSecondButtonLoading])

  useEffect(
    () => () => {
      if (secondButtonUnlockTimeoutRef.current !== null)
        window.clearTimeout(secondButtonUnlockTimeoutRef.current)
    },
    []
  )

  const handleSecondButtonClick = () => {
    if (isSecondButtonLocked || secondButtonDisabled || onSecondButtonLoading) return
    if (validateSecondButton && !validateSecondButton()) return
    if (!onSecondButtonClicked) return

    secondButtonLoadingStartedRef.current = false
    setIsSecondButtonLocked(true)

    if (secondButtonUnlockTimeoutRef.current !== null)
      window.clearTimeout(secondButtonUnlockTimeoutRef.current)

    secondButtonUnlockTimeoutRef.current = window.setTimeout(() => {
      if (!secondButtonLoadingStartedRef.current) {
        secondButtonUnlockTimeoutRef.current = null
        setIsSecondButtonLocked(false)
      }
    }, SAVE_CLICK_UNLOCK_FALLBACK_MS)

    try {
      onSecondButtonClicked()
    } catch (error) {
      if (secondButtonUnlockTimeoutRef.current !== null) {
        window.clearTimeout(secondButtonUnlockTimeoutRef.current)
        secondButtonUnlockTimeoutRef.current = null
      }
      setIsSecondButtonLocked(false)
      throw error
    }
  }

  return (
    <>
      <div
        className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
        data-testid="fixed-header"
      >
        <div className="flex h-14 items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={onBack}
              data-testid="fixed-header-back"
              className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              {resolvedBackLabel}
            </button>
            <div className="h-4 w-px bg-border" />
            <h1 className="text-lg font-semibold" data-testid="fixed-header-title">
              {title}
            </h1>
          </div>

          <div className="flex items-center gap-2" data-testid="fixed-header-actions">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 px-2.5 [&_svg]:size-3.5"
              onClick={onFirstButtonClicked}
              disabled={isFirstButtonDisabled || onFirstButtonLoading}
              data-testid="fixed-header-secondary-action"
            >
              {onFirstButtonLoading && <Loader2 className="animate-spin" />}
              {firstButtonText || t("cancel")}
            </Button>
            <Button
              size="sm"
              className="gap-1.5 px-2.5 [&_svg]:size-3.5"
              onClick={handleSecondButtonClick}
              disabled={secondButtonDisabled || onSecondButtonLoading || isSecondButtonLocked}
              data-testid="fixed-header-primary-action"
            >
              {onSecondButtonLoading ? <Loader2 className="animate-spin" /> : <Check />}
              {secondButtonText || t("save")}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
